import { EVENT_STATUS } from '@hedepy/common/enums/EVENT'
import dayjs, { type Dayjs } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import React, { useMemo } from 'react'
import { type Matcher } from 'react-day-picker'

import useEvents from '@/legacy-src/hooks/useEvents'

import { useTherapistCart } from '@/src/domains/cart/hooks/useTherapistCart'
import { ClientBookingMonthCalendar } from '@/src/domains/client-booking/components/ClientBookingMonthCalendar'
import { useClientBookingFreeSlots } from '@/src/domains/client-booking/hooks/useClientBookingFreeSlots'
import type { FreeSlotTypeFilterWithAvailability } from '@/src/domains/client-booking/types'

dayjs.extend(isBetween)

type Props = {
  therapistId: number
  serviceId: number
  type: FreeSlotTypeFilterWithAvailability
  day: Dayjs | null
  setDay: (day: Dayjs) => void
  month: Dayjs
  setMonth: (month: Dayjs) => void
}

const CalendarMonthView: React.FC<Props> = ({ day, setDay, month, setMonth, therapistId, serviceId, type }) => {
  const { selectedSlots } = useTherapistCart()

  const endOfMonth = month.endOf('month').endOf('day')

  const { slots, isLoading: isLoadingSlots } = useClientBookingFreeSlots({
    therapistId,
    serviceId,
    type,
    dateTo: endOfMonth.unix(),
  })

  const { events, isLoading: isLoadingEvents } = useEvents()

  const disabledMatcher: Matcher = useMemo(() => {
    if (isLoadingSlots || isLoadingEvents) return true

    const daysInMonth = month.daysInMonth()
    const enabledDates = {}

    // Enable days with available slots
    if (slots) {
      slots.forEach((slot) => {
        const dayjsStartDay = dayjs(slot.startDate)

        if (dayjsStartDay.isBetween(month, endOfMonth)) {
          enabledDates[dayjsStartDay.date()] = true
        }
      })
    }

    if (events) {
      events
        .filter(
          (event) =>
            event.status === EVENT_STATUS.RESERVED &&
            event.serviceId === serviceId &&
            event.therapistId === therapistId,
        )
        .forEach((event) => {
          const startDate = dayjs(event.startDate)

          if (startDate.isBetween(month, endOfMonth)) {
            enabledDates[startDate.date()] = true
          }
        })
    }

    const matcher: Matcher = []

    for (let day = 1; day <= daysInMonth; day++) {
      if (!enabledDates[day]) {
        matcher.push(month.date(day).toDate())
      }
    }

    return matcher
  }, [isLoadingSlots, slots, isLoadingEvents, events, month, serviceId, therapistId, endOfMonth])

  const hasAppointmentMatcher: Matcher = useMemo(() => {
    if (isLoadingEvents || !events) return false

    return events
      .filter(
        (event) =>
          (event.status === EVENT_STATUS.COMPLETED || event.status === EVENT_STATUS.RESERVED) &&
          event.serviceId === serviceId &&
          event.therapistId === therapistId,
      )
      .map((event) => dayjs(event.startDate).toDate())
  }, [isLoadingEvents, events, serviceId, therapistId])

  const hasSelectedSlotMatcher: Matcher = useMemo(() => {
    if (isLoadingSlots || !slots) return false

    return selectedSlots
      .map(({ startDate }) => slots.find((slot) => slot.startDate === startDate))
      .filter((slot) => !!slot)
      .map((slot) => dayjs(slot.startDate).toDate())
  }, [isLoadingSlots, slots, selectedSlots])

  return (
    <div className="mb-6">
      <ClientBookingMonthCalendar
        month={day ? month : dayjs()}
        onMonthChange={setMonth}
        day={day ?? dayjs()}
        onDaySelect={setDay}
        disabled={disabledMatcher}
        hasAppointment={hasAppointmentMatcher}
        hasSelectedSlot={hasSelectedSlotMatcher}
      />
    </div>
  )
}

export default CalendarMonthView
